import { useEffect, useState } from 'react';
import axios from 'axios';
import { Users, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const isCouncilWindow = () => {
  const d = new Date();
  // Show the card from the 1st through the 5th of the month
  return d.getDate() >= 1 && d.getDate() <= 5;
};

/**
 * Once-a-month Household Council card. Visible days 1-5 of each month.
 * Three prompts. Every member submits privately, results roll up together.
 */
export default function HouseholdCouncilCard({ month, membersCount = 1 }) {
  const [submitted, setSubmitted] = useState(false);
  const [responses, setResponses] = useState([]);
  const [busy, setBusy] = useState(false);
  const [win, setWin] = useState('');
  const [hardest, setHardest] = useState('');
  const [change, setChange] = useState('');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isCouncilWindow()) return;
    setVisible(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/budget/council/${month}`);
        setResponses(data.responses || []);
        setSubmitted(!!data.submitted);
      } catch { /* silent */ }
    })();
  }, [month]);

  if (!visible) return null;

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/budget/council/${month}`, {
        biggest_win: win, hardest_expense: hardest, one_change: change,
      });
      toast.success('Council response saved — thank you.');
      setSubmitted(true);
      const { data } = await axios.get(`${API_URL}/api/budget/council/${month}`);
      setResponses(data.responses || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save council response');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-sm border border-[#B76E79]/25 bg-gradient-to-br from-white to-[#FFF5F1] p-5 shadow-sm"
      data-testid="household-council-card"
    >
      <div className="flex items-center mb-2">
        <div className="w-9 h-9 rounded-full bg-[#B76E79]/12 flex items-center justify-center mr-3 flex-shrink-0">
          <Users className="w-4 h-4 text-[#B76E79]" strokeWidth={1.5} />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[#B76E79] font-['Manrope'] font-semibold">
            First-of-month ritual
          </p>
          <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#36454F]">
            Household Council
          </h3>
        </div>
      </div>

      {!submitted ? (
        <>
          <p className="text-sm text-[#36454F]/70 font-['Manrope'] mb-4">
            Three quick questions. Two minutes. Every member answers privately, the answers roll up together.
          </p>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-['Manrope'] font-semibold text-[#36454F]/70 uppercase tracking-wider">
                Biggest financial win this past month
              </label>
              <Textarea
                value={win}
                onChange={(e) => setWin(e.target.value)}
                placeholder="Paid off a card, tithed on time, cooked at home more…"
                maxLength={280}
                className="mt-1 text-sm font-['Manrope']"
                data-testid="council-win-input"
              />
            </div>
            <div>
              <label className="text-xs font-['Manrope'] font-semibold text-[#36454F]/70 uppercase tracking-wider">
                Hardest expense — no judgment
              </label>
              <Textarea
                value={hardest}
                onChange={(e) => setHardest(e.target.value)}
                placeholder="What tempted you off-budget most this month?"
                maxLength={280}
                className="mt-1 text-sm font-['Manrope']"
                data-testid="council-hardest-input"
              />
            </div>
            <div>
              <label className="text-xs font-['Manrope'] font-semibold text-[#36454F]/70 uppercase tracking-wider">
                One thing you'd change this month
              </label>
              <Textarea
                value={change}
                onChange={(e) => setChange(e.target.value)}
                placeholder="Cap dining at $100, tithe first, pack lunches Tues/Thur…"
                maxLength={280}
                className="mt-1 text-sm font-['Manrope']"
                data-testid="council-change-input"
              />
            </div>
          </div>
          <Button
            onClick={submit}
            disabled={busy || (!win && !hardest && !change)}
            className="mt-4 h-10 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm text-sm"
            data-testid="council-submit-btn"
          >
            {busy ? 'Saving…' : 'Submit council response'}
          </Button>
        </>
      ) : (
        <>
          <p className="text-sm text-emerald-700 font-['Manrope'] flex items-center mb-4">
            <Check className="w-4 h-4 mr-1.5" strokeWidth={2} />
            You&apos;ve submitted this month&apos;s council response.
          </p>
          {responses.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-widest text-[#36454F]/50 font-['Manrope'] font-semibold">
                Household responses ({responses.length}/{membersCount})
              </p>
              {responses.map((r) => (
                <div
                  key={r.user_id}
                  className="text-xs font-['Manrope'] border-l-2 border-[#B76E79]/30 pl-3 py-1 text-[#36454F]/75"
                  data-testid={`council-response-${r.user_id}`}
                >
                  <div className="font-semibold text-[#36454F] mb-0.5">{r.user_name?.split(' ')[0]}</div>
                  {r.biggest_win && <div>Win: {r.biggest_win}</div>}
                  {r.hardest_expense && <div>Hardest: {r.hardest_expense}</div>}
                  {r.one_change && <div>Change: {r.one_change}</div>}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
